/* eslint-disable react-refresh/only-export-components */
import { createContext, useContext, useEffect, useState } from "react";
import useAuth from "./auth-provider";
import { queryClient } from "@/hooks/use-query-client";
import { QueryKeys, STORAGE, STORAGE_KEY } from "@/constants";
import type { Demographics } from "@/model/demographics";
import { getUserLocation } from "@/lib/location-utils";
import { getItem, setItem } from "@/lib/storage-utils";
import {
  getLocationDataFromCoordinates,
  toPostalCode,
} from "@/services/location";

export type LocationType = {
  description: string;
  lat: number;
  lng: number;
  place_id: string;
  reference: string;
  postal_code: string;
};

type LocationContextType = {
  location: LocationType | null;
  isFetching: boolean;
  isFetched: boolean;
  permissionVersion: number;
  fetchAndSetLocation: () => void;
};

const LocationContext = createContext<LocationContextType | undefined>(
  undefined,
);

export const useUserLocation = (): LocationContextType => {
  const context = useContext(LocationContext);
  if (!context)
    throw new Error("useLocation must be used within LocationProvider");
  return context;
};

export const LocationProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const { authData } = useAuth();
  const [location, setLocation] = useState<LocationType | null>(null);
  const [isFetching, setIsFetching] = useState(false);
  const [isFetched, setIsFetched] = useState(false);
  const [permissionVersion, setPermissionVersion] = useState(0);
  const demographicsData = queryClient.getQueryData([
    QueryKeys.DEMOGRAPHICS,
    authData?.user?.username,
  ]) as Demographics | undefined;

  const DEFAULT_LOCATION = {
    latitude: Number(demographicsData?.latitude ?? 0),
    longitude: Number(demographicsData?.longitude ?? 0),
  };

  const fetchAndSetLocation = async () => {
    setIsFetching(true);

    try {
      const permissionStatus = await navigator.permissions.query({
        name: "geolocation",
      });

      let coords = DEFAULT_LOCATION;

      if (permissionStatus.state !== "denied") {
        try {
          const { location: userCoords } =
            await getUserLocation(DEFAULT_LOCATION);
          coords = userCoords;
        } catch (geoError) {
          console.warn(
            "Failed to get user location, falling back to default.",
            geoError,
          );
        }
      } else {
        console.warn("Geolocation permission denied — using default location.");
      }

      const geo = await getLocationDataFromCoordinates(
        coords.latitude,
        coords.longitude,
        authData!.user!.access_token,
      );
      const result = geo?.data?.results?.[0];

      const newLocation: LocationType = {
        description: result?.formatted_address || "Default Location",
        lat: coords.latitude,
        lng: coords.longitude,
        place_id: result?.place_id || "",
        reference: "",
        postal_code: toPostalCode(result?.address_components),
      };

      setLocation(newLocation);
      setIsFetched(true);
      setItem(STORAGE_KEY.LOCATION, newLocation, STORAGE.SESSION);
      setItem(STORAGE_KEY.LOCATION_FETCHED, true, STORAGE.SESSION);
    } catch (error) {
      console.error("Failed to fetch location data", error);
      // still fallback to default location if geolocation fails entirely
      const newLocation: LocationType = {
        description: "Default Location",
        lat: DEFAULT_LOCATION.latitude,
        lng: DEFAULT_LOCATION.longitude,
        place_id: "",
        reference: "",
        // Explicit, not accidental: an empty zip here is what lets the
        // service-layer fall back to the member's Demographics zip.
        postal_code: "",
      };

      setLocation(newLocation);
      setIsFetched(true);
      setItem(STORAGE_KEY.LOCATION, newLocation, STORAGE.SESSION);
      setItem(STORAGE_KEY.LOCATION_FETCHED, true, STORAGE.SESSION);
    } finally {
      setIsFetching(false);
    }
  };

  useEffect(() => {
    const savedLocation = getItem<LocationType>(
      STORAGE_KEY.LOCATION,
      STORAGE.SESSION,
    );
    const savedFetched = getItem<boolean>(
      STORAGE_KEY.LOCATION_FETCHED,
      STORAGE.SESSION,
    );

    if (savedLocation) {
      setLocation(savedLocation);
    }

    if (savedFetched) {
      setIsFetched(true);
    }

    if (!savedFetched) {
      fetchAndSetLocation();
    }

    navigator.permissions?.query({ name: "geolocation" }).then((status) => {
      status.onchange = () => {
        setPermissionVersion((v) => v + 1); // change signal
        fetchAndSetLocation();
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <LocationContext.Provider
      value={{
        location,
        isFetching,
        isFetched,
        permissionVersion,
        fetchAndSetLocation,
      }}
    >
      {children}
    </LocationContext.Provider>
  );
};
